/**
 * Email Processor Service
 *
 * API service for Email Attachment Processor feature
 */

import api from '../shared/services/api';
import {
  EmailConfig,
  EmailConfigCreate,
  EmailConfigUpdate,
  EmailConfigTestRequest,
  EmailConfigTestResponse,
  EmailThread,
  EmailThreadListResponse,
  ProcessingStats,
  ThreadListParams,
  CheckEmailsNowResponse,
} from '../types/emailProcessor';

// ============================================================================
// Email Configuration API
// ============================================================================

/**
 * Create a new email configuration
 */
export const createEmailConfig = async (data: EmailConfigCreate): Promise<EmailConfig> => {
  const response = await api.post('/api/v1/email-processor/config', data);
  return response.data;
};

/**
 * Get all email configurations for the current user
 */
export const getEmailConfigs = async (): Promise<EmailConfig[]> => {
  const response = await api.get('/api/v1/email-processor/config');
  return response.data;
};

/**
 * Get a specific email configuration
 */
export const getEmailConfig = async (configId: number): Promise<EmailConfig> => {
  const response = await api.get(`/api/v1/email-processor/config/${configId}`);
  return response.data;
};

/**
 * Update an email configuration
 */
export const updateEmailConfig = async (
  configId: number,
  data: EmailConfigUpdate
): Promise<EmailConfig> => {
  const response = await api.patch(`/api/v1/email-processor/config/${configId}`, data);
  return response.data;
};

/**
 * Delete an email configuration
 */
export const deleteEmailConfig = async (configId: number): Promise<void> => {
  await api.delete(`/api/v1/email-processor/config/${configId}`);
};

/**
 * Test email configuration without saving
 */
export const testEmailConfig = async (
  data: EmailConfigTestRequest
): Promise<EmailConfigTestResponse> => {
  const response = await api.post('/api/v1/email-processor/config/test', data);
  return response.data;
};

/**
 * Activate an email configuration (start monitoring)
 */
export const activateEmailConfig = async (configId: number): Promise<EmailConfig> => {
  const response = await api.post(`/api/v1/email-processor/config/${configId}/activate`);
  return response.data;
};

/**
 * Deactivate an email configuration (stop monitoring)
 */
export const deactivateEmailConfig = async (configId: number): Promise<EmailConfig> => {
  const response = await api.post(`/api/v1/email-processor/config/${configId}/deactivate`);
  return response.data;
};

// ============================================================================
// Email Threads API
// ============================================================================

/**
 * Get email threads with pagination and filtering
 */
export const getEmailThreads = async (
  params: ThreadListParams = {}
): Promise<EmailThreadListResponse> => {
  const response = await api.get('/api/v1/email-processor/threads', { params });
  return response.data;
};

/**
 * Get a specific email thread with all attachments
 */
export const getEmailThread = async (threadId: number): Promise<EmailThread> => {
  const response = await api.get(`/api/v1/email-processor/threads/${threadId}`);
  return response.data;
};

/**
 * Manually process an email thread
 */
export const processEmailThread = async (
  threadId: number,
  moduleId?: number
): Promise<any> => {
  const response = await api.post(`/api/v1/email-processor/threads/${threadId}/process`, {
    params: moduleId ? { module_id: moduleId } : {},
  });
  return response.data;
};

/**
 * Get full email thread details with conversation messages
 */
export const getEmailThreadDetails = async (threadId: number): Promise<EmailThread> => {
  const response = await api.get(`/api/v1/email-processor/threads/${threadId}`);
  return response.data;
};

/**
 * Download an attachment file
 */
export const getAttachmentFile = async (threadId: number, attachmentId: number): Promise<any> => {
  const response = await api.get(
    `/api/v1/email-processing/attachment/${threadId}/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  return response;
};

// ============================================================================
// Processing & Statistics API
// ============================================================================

/**
 * Manually trigger email check for a specific configuration
 */
export const checkEmailsNow = async (configId: number): Promise<CheckEmailsNowResponse> => {
  const response = await api.post(`/api/v1/email-processor/check-now/${configId}`);
  return response.data;
};

/**
 * Get processing statistics for the current user
 */
export const getProcessingStats = async (): Promise<ProcessingStats> => {
  const response = await api.get('/api/v1/email-processor/stats');
  return response.data;
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Get status color for email config
 */
export const getConfigStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    active: 'success',
    inactive: 'default',
    error: 'error',
    testing: 'info',
  };
  return colors[status] || 'default';
};

/**
 * Get status color for email thread
 */
export const getThreadStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'info',
    processing: 'warning',
    completed: 'success',
    failed: 'error',
    partially_processed: 'warning',
  };
  return colors[status] || 'default';
};

/**
 * Get status color for attachment
 */
export const getAttachmentStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'default',
    downloading: 'info',
    downloaded: 'info',
    processing: 'warning',
    completed: 'success',
    failed: 'error',
    skipped: 'default',
  };
  return colors[status] || 'default';
};

/**
 * Format processing time for display
 */
export const formatProcessingTime = (ms: number | null): string => {
  if (!ms) return 'N/A';

  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const emailProcessorService = {
  // Config
  createEmailConfig,
  getEmailConfigs,
  getEmailConfig,
  updateEmailConfig,
  deleteEmailConfig,
  testEmailConfig,
  activateEmailConfig,
  deactivateEmailConfig,
  // Threads
  getEmailThreads,
  getEmailThread,
  processEmailThread,
  getEmailThreadDetails,
  getAttachmentFile,
  // Processing
  checkEmailsNow,
  getProcessingStats,
  // Helpers
  formatFileSize,
  getConfigStatusColor,
  getThreadStatusColor,
  getAttachmentStatusColor,
  formatProcessingTime,
};

export default emailProcessorService;
